import { z } from 'zod'
import type { MeData } from '@/types/api.types'
import {
  MaritalStatus,
  type UpdateMePayload,
  UserGender,
  type UpdateMeContactInfo,
  type UpdateMePersonalInfo,
} from '@/types/user.types'

const emailOrEmpty = z.union([z.email('Invalid email'), z.literal('')]).optional()
const nameRegex = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/
const phoneRegex = /^\+?[0-9\s()-]{7,20}$/
const cnicRegex = /^\d{5}-\d{7}-\d$/
const passportRegex = /^[A-Z0-9]{6,20}$/
const postalCodeRegex = /^[A-Za-z0-9\s-]{3,12}$/
const isoAlpha2Regex = /^[A-Z]{2}$/

const genderSchema = z
  .union([
    z.literal(UserGender.MALE),
    z.literal(UserGender.FEMALE),
    z.literal(UserGender.OTHER),
    z.literal(''),
  ])
  .optional()

const maritalStatusSchema = z
  .union([
    z.literal(MaritalStatus.SINGLE),
    z.literal(MaritalStatus.MARRIED),
    z.literal(MaritalStatus.DIVORCED),
    z.literal(MaritalStatus.WIDOWED),
    z.literal(MaritalStatus.SEPARATED),
    z.literal(MaritalStatus.DOMESTIC_PARTNERSHIP),
    z.literal(MaritalStatus.OTHER),
    z.literal(''),
  ])
  .optional()

function optionalTrimmedString(max: number, message: string) {
  return z
    .union([z.string().trim().max(max, message), z.literal('')])
    .optional()
}

function optionalPatternString(
  regex: RegExp,
  invalidMessage: string,
  max: number,
  maxMessage: string
) {
  return z
    .union([
      z.string().trim().max(max, maxMessage).regex(regex, invalidMessage),
      z.literal(''),
    ])
    .optional()
}

const nameOrEmpty = z
  .union([
    z
      .string()
      .trim()
      .min(2, 'Enter at least 2 characters')
      .max(50, 'Use 50 characters or fewer')
      .regex(nameRegex, 'Use letters, spaces, apostrophes, or hyphens only'),
    z.literal(''),
  ])
  .optional()

const relationshipOrEmpty = z
  .union([
    z
      .string()
      .trim()
      .min(2, 'Enter at least 2 characters')
      .max(40, 'Use 40 characters or fewer')
      .regex(nameRegex, 'Use letters, spaces, apostrophes, or hyphens only'),
    z.literal(''),
  ])
  .optional()

const phoneOrEmpty = optionalPatternString(
  phoneRegex,
  'Enter a valid phone number',
  20,
  'Use 20 characters or fewer'
)

const isoAlpha2OrEmpty = z
  .union([
    z
      .string()
      .trim()
      .transform((value) => value.toUpperCase())
      .refine((value) => isoAlpha2Regex.test(value), 'Use a 2-letter country code, e.g. PK'),
    z.literal(''),
  ])
  .optional()

const dateOfBirthOrEmpty = z
  .union([
    z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a valid date')
      .refine((value) => !Number.isNaN(Date.parse(value)), 'Use a valid date')
      .refine((value) => new Date(value) <= new Date(), 'Date of birth cannot be in the future'),
    z.literal(''),
  ])
  .optional()

export const editMeSchema = z.object({
  firstName: nameOrEmpty,
  lastName: nameOrEmpty,
  personalInfo: z.object({
    dateOfBirth: dateOfBirthOrEmpty,
    gender: genderSchema,
    nationality: isoAlpha2OrEmpty,
    cnicNumber: optionalPatternString(
      cnicRegex,
      'Use CNIC format 12345-1234567-1',
      15,
      'Use CNIC format 12345-1234567-1'
    ),
    passportNumber: optionalPatternString(
      passportRegex,
      'Use 6-20 letters or numbers',
      20,
      'Use 20 characters or fewer'
    ),
    maritalStatus: maritalStatusSchema,
    emergencyContact: z.object({
      name: nameOrEmpty,
      relationship: relationshipOrEmpty,
      phone: phoneOrEmpty,
      email: emailOrEmpty,
    }),
  }),
  contactInfo: z.object({
    personalEmail: emailOrEmpty,
    phone: phoneOrEmpty,
    alternatePhone: phoneOrEmpty,
    address: z.object({
      street: optionalTrimmedString(120, 'Use 120 characters or fewer'),
      city: optionalTrimmedString(60, 'Use 60 characters or fewer'),
      state: optionalTrimmedString(60, 'Use 60 characters or fewer'),
      country: optionalTrimmedString(60, 'Use 60 characters or fewer'),
      postalCode: z
        .union([
          z
            .string()
            .trim()
            .max(12, 'Use 12 characters or fewer')
            .regex(postalCodeRegex, 'Use 3-12 letters, numbers, spaces, or hyphens'),
          z.literal(''),
        ])
        .optional(),
    }),
  }),
})

export type EditMeFormValues = z.infer<typeof editMeSchema>

function trim(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

function normalizeDate(value: string | undefined): string | undefined {
  const trimmed = trim(value)
  if (!trimmed) return undefined
  return trimmed.slice(0, 10)
}

function compactPersonalInfo(
  personalInfoValues: EditMeFormValues['personalInfo']
): UpdateMePersonalInfo | undefined {
  const emergencyContact = {
    name: trim(personalInfoValues.emergencyContact.name),
    relationship: trim(personalInfoValues.emergencyContact.relationship),
    phone: trim(personalInfoValues.emergencyContact.phone),
    email: trim(personalInfoValues.emergencyContact.email),
  }

  const personalInfo: UpdateMePersonalInfo = {
    dateOfBirth: normalizeDate(personalInfoValues.dateOfBirth),
    gender: trim(personalInfoValues.gender),
    nationality: trim(personalInfoValues.nationality)?.toUpperCase(),
    cnicNumber: trim(personalInfoValues.cnicNumber),
    passportNumber: trim(personalInfoValues.passportNumber)?.toUpperCase(),
    maritalStatus: trim(personalInfoValues.maritalStatus),
    emergencyContact: Object.values(emergencyContact).some(Boolean) ? emergencyContact : undefined,
  }

  const compactPersonalInfo = Object.fromEntries(
    Object.entries(personalInfo).filter(([, value]) => value !== undefined)
  ) as UpdateMePersonalInfo

  return Object.keys(compactPersonalInfo).length > 0 ? compactPersonalInfo : undefined
}

function compactContactInfo(
  contactInfoValues: EditMeFormValues['contactInfo']
): UpdateMeContactInfo | undefined {
  const address = {
    street: trim(contactInfoValues.address.street),
    city: trim(contactInfoValues.address.city),
    state: trim(contactInfoValues.address.state),
    country: trim(contactInfoValues.address.country),
    postalCode: trim(contactInfoValues.address.postalCode),
  }

  const compactAddress = Object.fromEntries(
    Object.entries(address).filter(([, value]) => value !== undefined)
  )

  const contactInfo: UpdateMeContactInfo = {
    personalEmail: trim(contactInfoValues.personalEmail),
    phone: trim(contactInfoValues.phone),
    alternatePhone: trim(contactInfoValues.alternatePhone),
    address: Object.keys(compactAddress).length > 0 ? compactAddress : undefined,
  }

  const compactContactInfo = Object.fromEntries(
    Object.entries(contactInfo).filter(([, value]) => value !== undefined)
  ) as UpdateMeContactInfo

  return Object.keys(compactContactInfo).length > 0 ? compactContactInfo : undefined
}

export function formValuesToUpdateMePayload(values: EditMeFormValues): UpdateMePayload {
  const payload: UpdateMePayload = {}

  const firstName = trim(values.firstName)
  if (firstName !== undefined) payload.firstName = firstName

  const lastName = trim(values.lastName)
  if (lastName !== undefined) payload.lastName = lastName

  const personalInfo = compactPersonalInfo(values.personalInfo)
  if (personalInfo) payload.personalInfo = personalInfo

  const contactInfo = compactContactInfo(values.contactInfo)
  if (contactInfo) payload.contactInfo = contactInfo

  return payload
}

export function meToEditFormValues(me: MeData): EditMeFormValues {
  const personalInfo = (me.personalInfo ?? {}) as Record<string, unknown>
  const contactInfo = (me.contactInfo ?? {}) as Record<string, unknown>
  const emergencyContact = (personalInfo.emergencyContact ?? {}) as Record<string, unknown>
  const address = (contactInfo.address ?? {}) as Record<string, unknown>
  const str = (value: unknown): string => (typeof value === 'string' ? value : '')
  const normalizeDateField = (value: unknown): string => {
    const raw = str(value).trim()
    if (!raw) return ''
    return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : raw
  }
  const normalizeGender = (value: unknown): EditMeFormValues['personalInfo']['gender'] => {
    const raw = str(value).trim().toUpperCase()
    return raw === UserGender.MALE || raw === UserGender.FEMALE || raw === UserGender.OTHER
      ? raw
      : ''
  }
  const normalizeMaritalStatus = (
    value: unknown
  ): EditMeFormValues['personalInfo']['maritalStatus'] => {
    const raw = str(value).trim().toUpperCase()
    return Object.values(MaritalStatus).includes(raw as keyof typeof MaritalStatus)
      ? (raw as EditMeFormValues['personalInfo']['maritalStatus'])
      : ''
  }
  const normalizeNationality = (value: unknown): string => {
    const raw = str(value).trim().toUpperCase()
    return isoAlpha2Regex.test(raw) ? raw : ''
  }

  return {
    firstName: me.firstName ?? '',
    lastName: me.lastName ?? '',
    personalInfo: {
      dateOfBirth: normalizeDateField(personalInfo.dateOfBirth),
      gender: normalizeGender(personalInfo.gender),
      nationality: normalizeNationality(personalInfo.nationality),
      cnicNumber: str(personalInfo.cnicNumber),
      passportNumber: str(personalInfo.passportNumber).toUpperCase(),
      maritalStatus: normalizeMaritalStatus(personalInfo.maritalStatus),
      emergencyContact: {
        name: str(emergencyContact.name),
        relationship: str(emergencyContact.relationship),
        phone: str(emergencyContact.phone),
        email: str(emergencyContact.email),
      },
    },
    contactInfo: {
      personalEmail: str(contactInfo.personalEmail),
      phone: str(contactInfo.phone),
      alternatePhone: str(contactInfo.alternatePhone),
      address: {
        street: str(address.street),
        city: str(address.city),
        state: str(address.state),
        country: str(address.country),
        postalCode: str(address.postalCode),
      },
    },
  }
}
